import { useMsal } from '@azure/msal-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'

import { loginRequest } from '../auth-config'
import type { AuthContextType } from '../contexts/AuthContext'
import { AuthContext } from '../contexts/AuthContext'

interface AuthProviderProps {
  children: ReactNode
}

// Local-dev bypass: skip Azure AD entirely and use a static token from .env. Enables running
// against a local backend whose host isn't whitelisted for MSAL. Guarded by an env var that is
// only set in the gitignored .env, so deployed builds always take the real MSAL path.
const BYPASS_MSAL = import.meta.env.VITE_BYPASS_MSAL === 'true'
const STATIC_API_TOKEN = import.meta.env.VITE_API_TOKEN ?? null

export function AuthProvider({ children }: Readonly<AuthProviderProps>) {
  const { instance, accounts } = useMsal()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(BYPASS_MSAL)

  const account = accounts[0] || null

  const displayName = account?.name
    ? account.name.split(' ')[0]
    : (account?.username.split('@')[0] ?? '')

  useEffect(() => {
    // Set active account if available
    if (account && !instance.getActiveAccount()) {
      instance.setActiveAccount(account)
    }
  }, [account, instance])

  const login = useCallback(async () => {
    // Local bypass: never contact Azure AD; treat the static token as an authenticated session.
    if (BYPASS_MSAL) {
      setIsAuthenticated(true)
      return
    }

    setIsLoading(true)
    setError(null)

    // try login silent first to get SSO experience using NAA behind the scenes
    // it automatically saves tokens into localStorage and is refreshed with same function
    try {
      const res = await instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
      })
      setIsAuthenticated(true)
      instance.setActiveAccount(res.account)
      return
    } catch (e) {
      console.error('AcquireTokenSilent failed. Error:', e)

      // try login popup if the above didn't work
      try {
        const response = await instance.acquireTokenPopup({
          scopes: loginRequest.scopes,
          prompt: 'login',
        })
        instance.setActiveAccount(response.account)
        setIsAuthenticated(true)
      } catch (popupError) {
        console.error('Interactive login failed:', popupError)
        const errorMessage =
          popupError instanceof Error ? popupError.message : 'Interactive login failed'
        setError(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }, [instance])

  const getAccessToken = useCallback(
    async (options: { forceRefresh?: boolean } = {}): Promise<string | null> => {
      const { forceRefresh = false } = options

      // Local bypass: hand the interceptor the static token instead of an MSAL access token.
      if (BYPASS_MSAL) {
        return STATIC_API_TOKEN
      }

      if (!account) {
        return null
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh,
        })

        return response.accessToken
      } catch (err) {
        console.error('Token acquisition failed:', err)

        if (forceRefresh) {
          setIsAuthenticated(false)
        }

        return null
      }
    },
    [account, instance]
  )

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    account,
    displayName,
    error,
    login,
    getAccessToken,
  }

  return <AuthContext value={contextValue}>{children}</AuthContext>
}
